import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, ExternalLink, RefreshCw, Info } from "lucide-react";
import api from "@/lib/api";
import { useCms } from "@/lib/cms";
import { Header } from "./Schools";

const TABS = [
  { id: "terms",         label: "Terms & Conditions",     field: "terms_custom",         url: "/terms" },
  { id: "privacy",       label: "Privacy Policy",         field: "privacy_custom",       url: "/privacy" },
  { id: "podcast_terms", label: "Podcast Terms",          field: "podcast_terms_custom", url: "/podcast-terms" },
];

export default function LegalCms() {
  const { reload } = useCms();
  const [tab, setTab]     = useState("terms");
  const [d, setD]         = useState({ terms_custom: "", privacy_custom: "", podcast_terms_custom: "" });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    api.get("/cms/legal").then((r) => { setD(r.data || {}); }).finally(() => setLoading(false));
  }, []);

  const active = TABS.find((t) => t.id === tab);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cms/legal", d);
      await reload();
      toast.success(`${active.label} saved.`);
    } catch { toast.error("Save failed."); }
    setSaving(false);
  };

  const resetToDefault = () => {
    if (!window.confirm(`Reset ${active.label} to default? This will clear your custom content and use the built-in text.`)) return;
    setD((prev) => ({ ...prev, [active.field]: "" }));
    toast("Custom content cleared. Click Save to apply.");
  };

  return (
    <div className="fade-up">
      <Header
        title="Legal Pages"
        subtitle="Edit Terms & Conditions, Privacy Policy, and Podcast Terms."
        actions={
          <div className="flex gap-2">
            <a href={active?.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50">
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-[#1e293b]">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="mt-6 flex gap-1 bg-gray-100 rounded-xl p-1 max-w-lg">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${tab === t.id ? "bg-white shadow text-[#0F172A]" : "text-gray-500 hover:text-gray-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Info banner */}
      <div className="mt-4 flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 max-w-3xl">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Leave the editor blank to use the built-in default content.
          Custom content (HTML supported) will override the default when saved.
          Use the Preview button to see how the page looks.
        </span>
      </div>

      {/* Editor */}
      <div className="mt-4 rounded-2xl border border-gray-200 bg-white overflow-hidden max-w-3xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
          <span className="text-sm font-medium text-gray-700">{active?.label}</span>
          <div className="flex gap-2">
            <button onClick={resetToDefault}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100">
              <RefreshCw className="w-3 h-3" /> Reset to default
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <textarea
            value={d[active.field] || ""}
            onChange={(e) => setD((prev) => ({ ...prev, [active.field]: e.target.value }))}
            placeholder={`Paste custom HTML content here for ${active.label}.\n\nLeave blank to use the built-in professional default content.`}
            className="w-full h-[60vh] p-5 text-sm font-mono text-gray-700 outline-none resize-none border-0"
            spellCheck={false}
          />
        )}
      </div>

      <p className="mt-3 text-xs text-gray-400 max-w-3xl">
        HTML tags are supported: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a href="..."&gt;, etc.
        Content is rendered safely in the public legal page.
      </p>
    </div>
  );
}
